"use client";

/* eslint-disable react-hooks/rules-of-hooks */

import ApiKeysDeleteDialog from "@/components/api-keys-delete";
import PowerOnPipelines from "@/components/icons/poweron-pipelines";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCurrentOrganization, useMembers } from "@/hooks/spacetime";
import { ApiKey } from "@/types/api-keys";
import { getProductNameByProduct, Product } from "@/types/subscription";
import { getInitials, getUserName } from "@/utils/utils";
import { ColumnDef } from "@tanstack/react-table";
import {
  CalendarDays,
  CircleAlert,
  Crown,
  IdCard,
  Key,
  Trash2,
} from "lucide-react";

export const columns = (
  hasActiveSubscription: boolean,
): ColumnDef<ApiKey>[] => [
  {
    accessorKey: "userId",
    size: 24,
    header: () => (
      <div className="flex items-center gap-1">
        <IdCard size={14} className="mt-0.5 text-muted" />
        Created By
      </div>
    ),
    cell: ({ row }) => {
      const { organization } = useCurrentOrganization();
      const { members } = useMembers();

      const member = members.find((m) => m.userId === row.original.userId)!;
      const name = getUserName(member.name, member.email);

      return (
        <div className="flex items-center gap-2">
          <Avatar className="size-7 rounded-full">
            <AvatarImage
              src={member.avatar ? `/avatars/${member.avatar}` : ""}
              alt={`Avatar for ${name}`}
            />
            <AvatarFallback className="rounded-full bg-card pb-0.5 text-xs font-medium text-muted">
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-none">
            <span className="flex items-center truncate py-2 font-semibold text-gray-1100 dark:text-gray-100">
              {name}
              {member.userId === organization?.ownerId && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Crown
                      className="ml-1 flex-shrink-0 text-yellow-400"
                      size={14}
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top" align="center">
                    Organization Owner
                  </TooltipContent>
                </Tooltip>
              )}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "createdAt",
    size: 18,
    header: () => (
      <div className="flex items-center gap-1">
        <CalendarDays size={14} className="mt-0.5 text-muted" />
        Created
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-2">
          {" "}
          {new Date(row.original.createdAt!).toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          })}
        </div>
      );
    },
  },
  {
    accessorKey: "apiKey",
    size: 28,
    header: () => (
      <div className="flex items-center gap-1">
        <Key size={14} className="mt-0.5 text-muted" />
        Secret&nbsp;Key
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="flex cursor-pointer items-center gap-2">
          {!hasActiveSubscription && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <CircleAlert className="mt-[2px] size-4 text-amber-500 dark:text-amber-400" />
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="center"
                  className="max-w-[14rem]"
                >
                  <p className="text-xs">
                    This API key cannot be used as there is no active
                    subscription
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {row.original.apiKey}
        </div>
      );
    },
  },
  {
    accessorKey: "product",
    size: 18,
    header: () => (
      <div className="flex items-center gap-1">
        <CalendarDays size={14} className="mt-0.5 text-muted" />
        Product
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-1">
          {row.original.product === "poweron-pipelines" && (
            <PowerOnPipelines className="mt-[1px] size-4" />
          )}
          {getProductNameByProduct(row.original.product as Product)}
        </div>
      );
    },
  },
  {
    id: "actions",
    size: 12,
    header: () => <></>,
    cell: ({ row }) => {
      return (
        <div className="flex items-center">
          <ApiKeysDeleteDialog
            apiKey={row.original}
            trigger={
              <Button
                variant="ghost"
                size="icon"
                className="size-8 text-red-500 hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-500/20"
              >
                <Trash2 />
              </Button>
            }
          />
        </div>
      );
    },
  },
];
